import type { FC } from 'react';
import React, { useContext } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import {
	ShortcutVisualizer,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import {
	NEXT_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_COMMENT_SHORTCUT,
	PREV_COMMENT_SHORTCUT,
	UNIVERSAL_PREV_COMMENT_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { fg } from '@confluence/feature-gating';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import {
	selectInlineComment,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';

import { InlineHeaderButton, NavigationContainer } from './styled-components';

const i18n = defineMessages({
	nextCommentLabel: {
		id: 'inline-comments-common.next-comment-label',
		description: 'Label displayed for the Next comment',
		defaultMessage: 'Next comment',
	},
	previousCommentLabel: {
		id: 'inline-comments-common.previous-comment-label',
		description: 'Label displayed for the Previous comment',
		defaultMessage: 'Previous comment',
	},
});

type CommentNavigationProps = {
	currentCommentIndex?: number;
	commentsCount?: number;
	isSSRFocusedComment?: boolean;
	disableCommentCount?: boolean;
	goToNextInlineComment: (arg?: boolean) => void;
	goToPrevInlineComment: (arg?: boolean) => void;
};

export const CommentNavigation: FC<CommentNavigationProps> = ({
	currentCommentIndex,
	commentsCount,
	isSSRFocusedComment,
	goToNextInlineComment,
	goToPrevInlineComment,
	disableCommentCount = false,
}) => {
	const { formatMessage } = useIntl();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();
	const pageClass = usePageClass();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const prevCommentShortcutStrings = useShortcutStrings({
		shortcut:
			isLiveEditMode || isOnEditRoute ? UNIVERSAL_PREV_COMMENT_SHORTCUT : PREV_COMMENT_SHORTCUT,
	});
	const nextCommentShortcutStrings = useShortcutStrings({
		shortcut:
			isLiveEditMode || isOnEditRoute ? UNIVERSAL_NEXT_COMMENT_SHORTCUT : NEXT_COMMENT_SHORTCUT,
	});

	const renderServerPlaceholder = useRenderServerPlaceholder();

	const noop = () => {};
	const renderCommentIndexAndCounts = () => {
		if (disableCommentCount || currentCommentIndex === -1) {
			return null;
		}
		/* doing typecheck instead of if(currentCommentIndex && commentsCount) since
    currentCommentIndex can be 0 */
		if (typeof currentCommentIndex === 'number' && typeof commentsCount === 'number') {
			return (
				// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
				<span data-testid="comment-index-and-counts">{`${
					currentCommentIndex + 1
				} of ${commentsCount}`}</span>
			);
		} else {
			return null;
		}
	};

	const isPageOrDoc = pageClass === 'renderer' || pageClass === 'editor';

	return (
		<NavigationContainer
			data-testid="comment-navigation-container"
			shouldHaveButtonGap={fg('confluence_frontend_content_wrapper')}
		>
			<Tooltip
				content={
					fg('platform_tooltip_shortcuts_migration_1') ? (
						<FormattedMessage {...i18n.previousCommentLabel} />
					) : (
						<ShortcutVisualizer
							contentBefore={<FormattedMessage {...i18n.previousCommentLabel} />}
							shortcut={
								isLiveEditMode || isOnEditRoute
									? UNIVERSAL_PREV_COMMENT_SHORTCUT
									: PREV_COMMENT_SHORTCUT
							}
							isTooltip
						/>
					)
				}
				shortcut={
					fg('platform_tooltip_shortcuts_migration_1') ? prevCommentShortcutStrings : undefined
				}
				hideTooltipOnClick
				position="top"
			>
				<InlineHeaderButton
					isSSRFocusedComment={isSSRFocusedComment}
					data-cy="inline-nav-previous"
					testId="comment-navigation-prev"
					iconBefore={
						<ChevronUpIcon
							label={formatMessage(i18n.previousCommentLabel)}
							color={token('color.icon.subtle')}
							spacing="spacious"
							size="small"
						/>
					}
					onClick={
						!isSSRFocusedComment
							? () => {
									selectInlineCommentCompoundExperience.selectAnnotation.start({
										pageClass,
										entryPoint: 'comment-navigation',
									});
									if (isPageOrDoc && fg('confluence_frontend_view_inline_comment_arrow_nav')) {
										experienceTracker.start({
											name: VIEW_INLINE_COMMENT_EXPERIENCE,
											timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
											attributes: {
												entryPoint: 'inline-comments-common/CommentNavigation',
												pageClass,
											},
										});
									}
									selectInlineComment.debug('Navigating to previous comment via button.');
									return goToPrevInlineComment();
								}
							: noop
					}
				/>
			</Tooltip>
			<Tooltip
				content={
					fg('platform_tooltip_shortcuts_migration_1') ? (
						<FormattedMessage {...i18n.nextCommentLabel} />
					) : (
						<ShortcutVisualizer
							contentBefore={<FormattedMessage {...i18n.nextCommentLabel} />}
							shortcut={
								isLiveEditMode || isOnEditRoute
									? UNIVERSAL_NEXT_COMMENT_SHORTCUT
									: NEXT_COMMENT_SHORTCUT
							}
							isTooltip
						/>
					)
				}
				shortcut={
					fg('platform_tooltip_shortcuts_migration_1') ? nextCommentShortcutStrings : undefined
				}
				hideTooltipOnClick
				position="top"
			>
				<InlineHeaderButton
					isSSRFocusedComment={isSSRFocusedComment}
					data-cy="inline-nav-next"
					testId="comment-navigation-next"
					iconBefore={
						<ChevronDownIcon
							label={formatMessage(i18n.nextCommentLabel)}
							color={token('color.icon.subtle')}
							spacing="spacious"
							size="small"
						/>
					}
					onClick={
						!isSSRFocusedComment
							? () => {
									selectInlineCommentCompoundExperience.selectAnnotation.start({
										pageClass,
										entryPoint: 'comment-navigation',
									});
									if (isPageOrDoc && fg('confluence_frontend_view_inline_comment_arrow_nav')) {
										experienceTracker.start({
											name: VIEW_INLINE_COMMENT_EXPERIENCE,
											timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
											attributes: {
												entryPoint: 'inline-comments-common/CommentNavigation',
												pageClass,
											},
										});
									}
									selectInlineComment.debug('Navigating to next comment via button.');
									return goToNextInlineComment();
								}
							: noop
					}
				/>
			</Tooltip>
			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
			{renderServerPlaceholder ? <span id="inline-comment-index" /> : renderCommentIndexAndCounts()}
		</NavigationContainer>
	);
};
